import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import GridItemMenu from './Items/_GridItemMenu'
import { itemDeleted, itemGroupDuplicated, itemMoved } from 'Vendor/Store/studio'
import { AppDispatch } from 'Vendor/Store/store'
import { selectSelectedItemIds, selectActivePageId } from 'Vendor/Store/studio/selectors'

interface GroupSelectionOverlayProps {
    bounds: { left: number; top: number; width: number; height: number }
    gridRef: React.RefObject<HTMLDivElement>
    dynamicRowHeight: number
    activeGridColumns: number
    zoomLevel: number
    selectedItems: any[]
}

const GroupSelectionOverlay: React.FC<GroupSelectionOverlayProps> = ({
    bounds,
    gridRef,
    dynamicRowHeight,
    activeGridColumns,
    zoomLevel,
    selectedItems,
}) => {
    const dispatch = useDispatch<AppDispatch>()
    const selectedItemIds = useSelector(selectSelectedItemIds)
    const activePageId = useSelector(selectActivePageId)

    const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null)
    const [originalPositions, setOriginalPositions] = useState<Record<string, { col_start: number; row_start: number }>>({})
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
    const [isDragging, setIsDragging] = useState(false)

    const handlePointerDown = (e: React.PointerEvent) => {
        e.stopPropagation()
        setDragStart({ x: e.clientX, y: e.clientY })
        setIsDragging(true)

        const positions: Record<string, { col_start: number; row_start: number }> = {}
        selectedItems.forEach(item => {
            positions[item.id] = { col_start: item.position.col_start, row_start: item.position.row_start }
        })
        setOriginalPositions(positions)
    }

    const handlePointerMove = (e: React.PointerEvent) => {
        if (!dragStart || !gridRef.current) return
        e.preventDefault()

        const dxCanvas = (e.clientX - dragStart.x) / zoomLevel
        const dyCanvas = (e.clientY - dragStart.y) / zoomLevel
        setDragOffset({ x: dxCanvas, y: dyCanvas })

        const columnWidth = gridRef.current.offsetWidth / activeGridColumns
        selectedItems.forEach(item => {
            const orig = originalPositions[item.id]
            if (!orig) return

            const deltaCol = dxCanvas / columnWidth
            const deltaRow = dyCanvas / dynamicRowHeight

            // Temporary update using transform on each item
            const el = document.querySelector(`[data-id="${item.id}"]`) as HTMLElement
            if (el) {
                el.style.transform = `translate3d(${deltaCol * columnWidth}px, ${deltaRow * dynamicRowHeight}px, 0)`
                el.style.zIndex = '9999'
            }
        })
    }

    const handlePointerUp = () => {
        if (!dragStart || !gridRef.current || !activePageId) {
            resetDrag()
            return
        }

        const columnWidth = gridRef.current.offsetWidth / activeGridColumns

        selectedItems.forEach(item => {
            const orig = originalPositions[item.id]
            if (!orig) return

            const deltaCol = Math.round(dragOffset.x / columnWidth)
            const deltaRow = Math.round(dragOffset.y / dynamicRowHeight)

            dispatch(itemMoved({
                pageId: activePageId,
                itemId: item.id,
                newPosition: {
                    col_start: orig.col_start + deltaCol,
                    row_start: orig.row_start + deltaRow,
                }
            }))

            const el = document.querySelector(`[data-id="${item.id}"]`) as HTMLElement
            if (el) {
                el.style.transform = ''
                el.style.zIndex = ''
            }
        })

        resetDrag()
    }

    const resetDrag = () => {
        setDragStart(null)
        setOriginalPositions({})
        setDragOffset({ x: 0, y: 0 })
        setIsDragging(false)
    }

    const handleDelete = () => {
        if (!activePageId) return
        dispatch(itemDeleted({ pageId: activePageId }))
    }

    const handleDuplicate = () => {
        if (!activePageId) return
        dispatch(itemGroupDuplicated({ pageId: activePageId, itemIds: selectedItemIds }))
    }

    return (
        <div
            className="group-selection-overlay"
            style={{
                position: 'absolute',
                left: bounds.left,
                top: bounds.top,
                width: bounds.width,
                height: bounds.height,
                border: '2px solid #1e69dbff',
                background: 'rgba(13,110,253,0.05)',
                boxSizing: 'border-box',
                pointerEvents: 'all',
                cursor: 'grab',
                zIndex: isDragging ? 9999 : 999,
                transform: `translate(${dragOffset.x}px, ${dragOffset.y}px)`,
            }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerLeave={handlePointerUp}
        >
            <div
                onPointerDown={(e) => e.stopPropagation()}
                style={{ position: 'absolute', top: -30, left: 0 }}
            >
                <GridItemMenu
                    onDuplicate={handleDuplicate}
                    onDelete={handleDelete}
                    onRotate={() => {}}
                />
            </div>

            {/* Group resize handles */}
            <div className="resize-handle top-left" />
            <div className="resize-handle top-center" />
            <div className="resize-handle top-right" />
            <div className="resize-handle middle-left" />
            <div className="resize-handle middle-right" />
            <div className="resize-handle bottom-left" />
            <div className="resize-handle bottom-center" />
            <div className="resize-handle bottom-right" />
        </div>
    )
}

export default GroupSelectionOverlay
